import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthService } from '../core/auth.service';
import { ConfigService } from '../core/config.service';
import { ClimateMetaData } from '../core/climate-readings.service';

export interface Location {
  location_name: string;
  location_id: string;
  time_zone?: string;
  meta_climate?: ClimateMetaData;
}

export interface Locations {
  locations: Location[];
}

export interface AppId {
  location_id: string;
  app_id: string;
}

export interface AdvancedSettings {
  ac_night_start: string;
  ac_night_end: string;
  occ_night_start: string;
  occ_night_end: string;
  occ_timeout_night_mins: number;
  occ_timeout_day_mins: number;
  door_sensor_timeout_mins: number;
  hysteresis_off_degrees: number;
  hysteresis_on_degrees: number;
  actuation_timeout_seconds: number;
  occupancy_sensitivity: number; // only two states, LOW (0) or HIGH (1)
  glitch_filter_probation_period: number;
  glitch_filter_hot_timeout: number;
  softstart_wait_time: number;
}

export interface HiddenSettings {
  emon_granular_interval_secs: number;
  emon_totals_interval_mins: number;
  emon_send_fine_grained: boolean;
}

export interface Settings {
  max_temp: number;
  min_temp: number;
  unoccupied_temp: number;
  automation_mode: string;
  hide_setpoint: boolean;
  ac_night_start?: string;
  ac_night_end?: string;
  occ_night_start?: string;
  occ_night_end?: string;
  occ_timeout_day_mins?: number;
  occ_timeout_night_mins?: number;
  door_sensor_timeout_mins?: number;
  emon_granular_interval_secs?: number;
  emon_totals_interval_mins?: number;
  emon_send_fine_grained?: boolean;
  // technically not a setting, but appended to GateWay Settings
  ac_model_used?: string;
  hysteresis_off_degrees: number;
  hysteresis_on_degrees: number;
  actuation_timeout_seconds: number;
  occupancy_sensitivity: number; // only two states, LOW (0) or HIGH (1)
  glitch_filter_probation_period: number;
  glitch_filter_hot_timeout: number;
  softstart_wait_time: number;
  // used by the automation set settings, temp workaround before deprecating Dashboard v1
  location_id?: string;
}

export interface LocationSettings {
  location_id: string;
  settings: Settings;
}

@Injectable()
export class LocationService {
  newLocationsAPIBaseUrl = this.config.getLocationServiceUrl();

  locationsCache: { [key: string]: Location } = {}; // cache locations for each loaded location, invalidated on reload

  constructor(
    private auth: AuthService,
    private config: ConfigService,
    private httpClient: HttpClient
  ) {}

  async getAllLocationsByAdmin() {
    const url = this.newLocationsAPIBaseUrl + '/location-service/list';

    const rawLocations = await this.httpClient.get<Array<any>>(url).toPromise();
    // TODO: there must be a way to shorten this
    return rawLocations.map(location => {
      return {
        location_id: location.location_id,
        location_name: location.location_name
      };
    });
  }

  async getLocationsForUser() {
    const locations = JSON.parse(localStorage.getItem('locations'));
    // in localStorage we can only store strings, so must parse
    if (locations === null) {
      try {
        const data = await this.auth.fetchUserP();
        return data.locations;
      } catch (error) {
        throw error;
      }
    }
    return locations;
  }

  async getLocationByIdP(locationId): Promise<Location> {
    let location,
      cachedLocation = this.locationsCache[locationId];
    if (cachedLocation) {
      location = cachedLocation;
    } else {
      const url = `${this.config.getBaseUrl()}/locations/${locationId}`;
      location = await this.httpClient.get<Location>(url).toPromise();
      this.locationsCache[locationId] = location;
    }
    return Promise.resolve(location); // returning a Promise to keep the signature
  }

  // we only need AppId for initializing shadows, otherwise can get settings straight from locationId
  getAppIdForLocationP(locationId) {
    const url = `${this.config.getBaseUrl()}/locations/${locationId}/appId`;
    return this.httpClient.get<AppId>(url).toPromise();
  }

  getSettingsForLocationP(locationId) {
    const url = `${this.config.getBaseUrl()}/locations/${locationId}/settings`;
    return this.httpClient.get<LocationSettings>(url).toPromise();
  }

  putSettingsForLocationP(locationId, reqBody: Settings) {
    const settingsUrl = this.config.getSettingsUrl();
    const url = this.auth.isAdmin()
      ? `${settingsUrl}/locations/${locationId}/settings/admin`
      : `${settingsUrl}/locations/${locationId}/settings`;

    return this.httpClient.post<Settings>(url, reqBody).toPromise();
  }
}



// WEBPACK FOOTER //
// /Users/sensorflow/Documents/legacy/kalinda/Dashboard/sensorflow-dashboard/src/app/locations/location.service.ts